import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import moment from "moment/moment";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { FileDownloadOutlined, OpenInNewOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { masterApi } from "../../apis/masterApi";
import { useGlobalContext } from "../../context/global";
import useUserDetails from "../../helper/userData/useUserDetails";
import ExportDownloadTooltip, {
  EXPORT_LIMIT_TOOLTIP_MESSAGE,
  EXPORT_TRACK_TOOLTIP_MESSAGE,
  getExportRedirectOutlinedButtonSx,
} from "../../components/Reports/Components/ExportDownloadTooltip";
import {
  buildHistoryLogsExportQueryBody,
  exportHistoryLogsReport,
} from "../../helper/PageWiseExportLogic/historyLogsExport";
import { encodeData } from "../../helper/common/encodeData";
import {
  buildFiltersArray,
  createFilterRow,
  serializeFilterRowValue,
} from "../../helper/common/buildSearchFilters";
import { formatDateFn } from "../../helper/common/formatDateForAPI";
import { openRequestTrackingPage } from "../../helper/common/openRequestTrackingPage";
import { getDefaultCalendarDate } from "../../constant/calendarDefaultConfigurations";
import Header from "../../components/header/header";
import MultiFilterSearch, {
  AppliedFilterChip,
} from "../../components/MultiFilterSearch/MultiFilterSearch";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import HistoryLogsTable from "./HistoryLogsTable";
import "./historyLogs.css";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];
const DEFAULT_SEARCH_TYPE = "campaign_name";
const TOOLBAR_CONTROL_HEIGHT = 36;

const downloadButtonSx = {
  height: TOOLBAR_CONTROL_HEIGHT,
  minHeight: TOOLBAR_CONTROL_HEIGHT,
  maxHeight: TOOLBAR_CONTROL_HEIGHT,
  boxSizing: "border-box",
  px: 1.25,
  py: 0,
  borderRadius: "4px",
  border: "1px solid #c4c4c4 !important",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "12px",
  lineHeight: 1.2,
  color: "#333333",
  bgcolor: "#fff",
  flexShrink: 0,
  boxShadow: "none",
  "& .MuiButton-startIcon": {
    mr: 0.5,
    ml: 0,
  },
  "&:hover": {
    border: "1px solid #c4c4c4 !important",
    bgcolor: "#EAE6FE",
    boxShadow: "none",
  },
  "&.Mui-focusVisible": {
    border: "1px solid #c4c4c4 !important",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    border: "1px solid #c4c4c4 !important",
    color: "#333333",
    bgcolor: "#fff",
    opacity: 0.7,
  },
};

const STATUS_OPTIONS = [
  { label: "Succeeded", value: "SUCCEEDED" },
  { label: "Skipped", value: "SKIPPED" },
  { label: "Failed", value: "FAILED" },
];

const SEARCH_TYPE_OPTIONS = [
  { label: "Campaign Name", value: "campaign_name", operator: "CONTAINS", inputType: "text" },
  { label: "Ad Group Name", value: "adgroup_name", operator: "CONTAINS", inputType: "text" },
  { label: "Change Type", value: "entity_change", operator: "CONTAINS", inputType: "text" },
  { label: "Tracking ID", value: "tracking_id", operator: "EQUALS", inputType: "text" },
  { label: "ASIN", value: "asin", operator: "IN", inputType: "text" },
  {
    label: "Status",
    value: "status",
    operator: "IN",
    inputType: "multiSelect",
    placeholder: "Select status",
    choices: STATUS_OPTIONS,
  },
];

const TABLE_COLUMNS = [
  { label: "Date", id: "created_at", width: 100, isSticky: true, sortable: true },
  { label: "Change Type", id: "entity_change", width: 140, isSticky: true},
  { label: "Campaign", id: "campaign_name", width: 200, isSticky: true },
  { label: "Ad Group", id: "adgroup_name", width: 180 },
  { label: "Tracking ID", id: "tracking_id", width: 160 },
  { label: "Old Value", id: "from_value", width: 180 },
  { label: "New Value", id: "to_value", width: 180 },
  { label: "Message", id: "message", width: 240 },
  { label: "Triggered By", id: "email", width: 120 },
  { label: "Status", id: "status", width: 110 },
];

function filterProductLogs(data) {
  return data?.filter((item) => !item?.entity_change?.includes("product"));
}

function parseHistoryLogsResponse(result) {
  const payload = result?.data;
  if (Array.isArray(payload)) {
    return {
      data: filterProductLogs(payload),
      totalCount: result?.total_count ?? null,
    };
  }
  if (payload && Array.isArray(payload?.data)) {
    return {
      data: filterProductLogs(payload.data),
      totalCount:
        payload?.total_count ??
        payload?.totalCount ??
        payload?.total ??
        result?.total_count ??
        null,
    };
  }
  return { data: [], totalCount: null };
}

function formatDisplayDate(dateString) {
  if (!dateString) return "-";
  return moment(dateString).format("DD MMMM hh:mm A");
}

function buildHistoryLogsRequest({
  limit,
  page,
  activeFilters,
  sortField,
  sortDirection,
  calendarDate,
  userPlatform,
}) {
  const isTrackingIdSearch = activeFilters.some(
    (filter) =>
      filter.searchType === "tracking_id" && Boolean(filter.searchText?.trim())
  );

  const requestData = {
    source: userPlatform,
    limit,
    page,
    order_by: sortField,
    sorting_order: sortDirection,
  };

  if (!isTrackingIdSearch) {
    requestData.start_date = formatDateFn(calendarDate[0].startDate);
    requestData.end_date = formatDateFn(calendarDate[0].endDate);
  }

  const filters = buildFiltersArray(activeFilters, SEARCH_TYPE_OPTIONS);
  if (filters.length) {
    requestData.filtering = encodeData(filters);
  }

  return requestData;
}

function uniqueEntityIds(rows, key) {
  return [
    ...new Set(
      rows
        .map((row) => row?.[key])
        .filter((id) => id != null && id !== "")
        .map(String)
    ),
  ];
}

async function fetchEntityNameMapByIds(ids, listType, userPlatform) {
  if (!ids.length) return {};

  const result = await masterApi(
    {
      source: userPlatform,
      limit: ids.length,
      fields: "name,id",
      filtering: encodeData([
        { field_name: "id", operator: "IN", values: ids },
      ]),
    },
    listType,
    false
  );

  return (result?.data?.data ?? []).reduce((map, item) => {
    if (item?.id != null) {
      map[String(item.id)] = item.name;
    }
    return map;
  }, {});
}

async function fetchHistoryLogNameMaps(rows, userPlatform) {
  const campaignIds = uniqueEntityIds(rows, "campaign_id");
  const adsetIds = uniqueEntityIds(rows, "adset_id");

  const [campaignNameById, adgroupNameById] = await Promise.all([
    fetchEntityNameMapByIds(campaignIds, "campaignList", userPlatform),
    fetchEntityNameMapByIds(adsetIds, "adsetList", userPlatform),
  ]);

  return { campaignNameById, adgroupNameById };
}

function enrichHistoryLogRow(data, campaignNameById, adgroupNameById) {
  return {
    ...data,
    campaign_name:
      data?.campaign_name ??
      campaignNameById?.[String(data?.campaign_id)] ??
      "-",
    adgroup_name:
      data?.adgroup_name ??
      adgroupNameById?.[String(data?.adset_id)] ??
      "-",
    created_at_display: formatDisplayDate(data?.created_at),
  };
}

const HistoryLogs = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const userPlatform = JSON.parse(localStorage.getItem("user_platform"));
  const { clientId } = useGlobalContext();
  const { userDetails } = useUserDetails();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTrackingId = searchParams.get("tracking_id")?.trim() || "";
  const initialFilterRows = initialTrackingId
    ? [createFilterRow("tracking_id", initialTrackingId)]
    : [createFilterRow(DEFAULT_SEARCH_TYPE)];
  const initialAppliedFilters = initialTrackingId
    ? [{ searchType: "tracking_id", searchText: initialTrackingId }]
    : [];

  // Page-local calendar only — does not mutate global calendar context
  const [calendarDate, setCalendarDate] = useState(() => getDefaultCalendarDate(true));
  const [applyCalendar, setApplyCalendar] = useState(1);

  const [rawNotificationsData, setRawNotificationsData] = useState([]);
  const [isLoaderVisible, setIsLoaderVisible] = useState(true);
  const [campaignNameById, setCampaignNameById] = useState({});
  const [adgroupNameById, setAdgroupNameById] = useState({});
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExportRedirectMode, setIsExportRedirectMode] = useState(false);
  const [pageLimit, setPageLimit] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [filterRows, setFilterRows] = useState(initialFilterRows);
  const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters);
  const [orderBy, setOrderBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const isFirstPageLimitEffect = useRef(true);
  const appliedFiltersRef = useRef(appliedFilters);
  appliedFiltersRef.current = appliedFilters;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageLimit));

  const hasActiveSearch = appliedFilters.length > 0;

  const notificationsDataToShow = useMemo(() => {
    return rawNotificationsData.map((data) => {
      const trackingId = data?.tracking_id;
      const enriched = enrichHistoryLogRow(
        data,
        campaignNameById,
        adgroupNameById
      );

      return {
        ...enriched,
        tracking_id_display: trackingId
          ? trackingId.length > 20
            ? `${trackingId.slice(0, 18)}...`
            : trackingId
          : "-",
      };
    });
  }, [rawNotificationsData, campaignNameById, adgroupNameById]);

  const updateTotalCount = useCallback(
    (filteredLength, apiPage, limit, totalFromApi) => {
      if (totalFromApi !== null && totalFromApi !== undefined) {
        setTotalCount(totalFromApi);
        return;
      }
      if (filteredLength < limit) {
        setTotalCount(apiPage * limit + filteredLength);
        return;
      }
      setTotalCount((prev) => Math.max(prev, (apiPage + 1) * limit + 1));
    },
    []
  );

  const fetchNotifications = useCallback(
    async ({
      pageNumber = 1,
      limit = pageLimit,
      isInitial = false,
      activeFilters = appliedFiltersRef.current,
      sortField = orderBy,
      sortDirection = sortOrder,
    } = {}) => {
      const apiPage = pageNumber - 1;
      const requestData = buildHistoryLogsRequest({
        limit,
        page: apiPage,
        activeFilters,
        sortField,
        sortDirection,
        calendarDate,
        userPlatform,
      });

      if (isInitial) {
        setIsLoaderVisible(true);
      } else {
        setPaginationLoading(true);
      }

      try {
        const result = await masterApi(requestData, "historyLogs", false);
        const { data: filteredData, totalCount: totalFromApi } =
          parseHistoryLogsResponse(result);

        const { campaignNameById: nextCampaignNames, adgroupNameById: nextAdgroupNames } =
          await fetchHistoryLogNameMaps(filteredData, userPlatform);

        setCampaignNameById(nextCampaignNames);
        setAdgroupNameById(nextAdgroupNames);
        setRawNotificationsData(filteredData);
        updateTotalCount(filteredData.length, apiPage, limit, totalFromApi);
        setCurrentPage(pageNumber);
      } catch (error) {
        console.log(error);
      } finally {
        if (isInitial) {
          setIsLoaderVisible(false);
        } else {
          setPaginationLoading(false);
        }
      }
    },
    [pageLimit, orderBy, sortOrder, calendarDate, updateTotalCount]
  );

  useEffect(() => {
    const trackingId = searchParams.get("tracking_id")?.trim() || "";
    if (trackingId) {
      const nextRows = [createFilterRow("tracking_id", trackingId)];
      setFilterRows(nextRows);
      setAppliedFilters([{ searchType: "tracking_id", searchText: trackingId }]);
    }
  }, [searchParams]);

  const handleLocalCalendarChange = useCallback(
    ({ calendarDate: nextCalendarDate, applyCalendar: nextApplyCalendar }) => {
      if (nextCalendarDate) {
        setCalendarDate(nextCalendarDate);
      }
      if (typeof nextApplyCalendar === "number") {
        setApplyCalendar(nextApplyCalendar);
      }
    },
    []
  );

  useEffect(() => {
    const isInitialLoad = isFirstPageLimitEffect.current;
    isFirstPageLimitEffect.current = false;
    fetchNotifications({ pageNumber: 1, isInitial: isInitialLoad });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimit, appliedFilters, orderBy, sortOrder, applyCalendar]);

  const handlePageChange = (pageNumber) => {
    fetchNotifications({ pageNumber, isInitial: false });
  };

  const handleSort = (field, direction) => {
    setOrderBy(field);
    setSortOrder(direction);
  };

  const handleApplyFilters = () => {
    const nextAppliedFilters = filterRows
      .map((row) => ({
        searchType: row.searchType,
        searchText: serializeFilterRowValue(row, SEARCH_TYPE_OPTIONS),
      }))
      .filter((row) => row.searchText);
    setAppliedFilters(nextAppliedFilters);
  };

  const handleClearAllFilters = () => {
    setFilterRows([createFilterRow(DEFAULT_SEARCH_TYPE)]);
    setAppliedFilters([]);
    if (searchParams.get("tracking_id")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("tracking_id");
      setSearchParams(nextParams, { replace: true });
    }
  };

  const handleRemoveAppliedFilter = (index) => {
    const nextApplied = appliedFilters.filter((_, itemIndex) => itemIndex !== index);
    setAppliedFilters(nextApplied);
    setFilterRows(
      nextApplied.length
        ? nextApplied.map((filter) =>
            createFilterRow(filter.searchType, filter.searchText)
          )
        : [createFilterRow(DEFAULT_SEARCH_TYPE)]
    );
    if (
      appliedFilters[index]?.searchType === "tracking_id" &&
      searchParams.get("tracking_id")
    ) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("tracking_id");
      setSearchParams(nextParams, { replace: true });
    }
  };

  const handleDownloadReport = async () => {
    if (isExportRedirectMode) {
      openRequestTrackingPage();
      return;
    }

    const exportTotalCount = totalCount || rawNotificationsData?.length || 0;
    if (exportTotalCount <= 0) {
      toast.info("No records available for export.");
      return;
    }

    await exportHistoryLogsReport({
      clientId,
      userPlatform,
      triggeredBy:
        userDetails?.user?.email ?? localStorage.getItem("email") ?? "",
      totalCount: exportTotalCount,
      queryBody: buildHistoryLogsExportQueryBody({
        activeFilters: appliedFiltersRef.current,
        sortField: orderBy,
        sortDirection: sortOrder,
        calendarDate,
        searchTypeOptions: SEARCH_TYPE_OPTIONS,
      }),
      setIsExporting: setIsDownloading,
      onExportSuccess: () => setIsExportRedirectMode(true),
    });
  };

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box sx={{ px: 0, pb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            History Logs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Track campaign and ad group changes made across your Amazon
            account.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <MultiFilterSearch
              searchTypeOptions={SEARCH_TYPE_OPTIONS}
              defaultSearchType={DEFAULT_SEARCH_TYPE}
              filterRows={filterRows}
              onFilterRowsChange={setFilterRows}
              onApply={handleApplyFilters}
              onCreateRow={createFilterRow}
            />
          </Box>

          <Box
            className="history-logs-toolbar-actions"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <ExportDownloadTooltip
              autoOpen={isExportRedirectMode}
              title={
                isExportRedirectMode
                  ? EXPORT_TRACK_TOOLTIP_MESSAGE
                  : EXPORT_LIMIT_TOOLTIP_MESSAGE
              }
            >
              <span>
                <Button
                  variant="outlined"
                  onClick={handleDownloadReport}
                  disabled={isDownloading}
                  startIcon={
                    isDownloading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : isExportRedirectMode ? (
                      <OpenInNewOutlined sx={{ fontSize: 16 }} />
                    ) : (
                      <FileDownloadOutlined sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={getExportRedirectOutlinedButtonSx(
                    isExportRedirectMode,
                    downloadButtonSx
                  )}
                >
                  {isDownloading
                    ? "Downloading"
                    : isExportRedirectMode
                      ? "Track"
                      : "Download"}
                </Button>
              </span>
            </ExportDownloadTooltip>
            <Header
              showOnlyCalendar={true}
              includeToday={true}
              localCalendar={true}
              defaultCalendarDate={getDefaultCalendarDate(true)}
              defaultDateDifference="Last 7 Days"
              onLocalCalendarChange={handleLocalCalendarChange}
            />
          </Box>
        </Box>

        {hasActiveSearch && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
            {appliedFilters.map((filter, index) => (
              <AppliedFilterChip
                key={`${filter.searchType}-${filter.searchText}-${index}`}
                filter={filter}
                searchTypeOptions={SEARCH_TYPE_OPTIONS}
                onDelete={() => handleRemoveAppliedFilter(index)}
              />
            ))}
            {appliedFilters.length > 1 && (
              <Chip
                size="small"
                variant="outlined"
                label="Clear all"
                onClick={handleClearAllFilters}
              />
            )}
          </Box>
        )}

        <HistoryLogsTable
          columns={TABLE_COLUMNS}
          rows={notificationsDataToShow}
          isLoading={isLoaderVisible || paginationLoading}
          orderBy={orderBy}
          order={sortOrder}
          onSort={handleSort}
        />

        {!isLoaderVisible &&
          !paginationLoading &&
          (rawNotificationsData?.length > 0 || totalCount > 0) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                py: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#4f5366",
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "12px" }}>
                  Page Size
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageLimit}
                    onChange={(e) => setPageLimit(Number(e.target.value))}
                    disabled={paginationLoading}
                    sx={{
                      height: 36,
                      fontSize: "12px",
                      color: "#333333",
                    }}
                    MenuProps={{ disableScrollLock: true }}
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option} sx={{ fontSize: "12px" }}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                lazyLoading={paginationLoading}
                totalItems={totalCount}
                itemsPerPage={pageLimit}
              />
            </Box>
          )}
      </Box>
    </div>
  );
};

export default HistoryLogs;
